import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { Navbar, Container, NavDropdown } from 'react-bootstrap';
import { useToast } from '../../hooks/useToast';
import { useServerHeaderClock } from '../../hooks/useServerHeaderClock';
import { ReceptionToastStack } from '../receptionist/ui';
import { useAuth } from '../../context/AuthContext';
import { authService } from '../../services/api';
import { getRoleLabel } from '../../constants/roles';
import { PLATFORM_BRANDING } from '../../constants/branding';
import { nursingConsultationEditPath, nursingFilePath } from './nursePaths';
import { CLINICAL_WORK_SITE_SESSION_KEY } from './nurseWorkspaceStorage';
import { NurseWorkspaceContext } from './nurseWorkspaceContext';
import { useNurseSiteQueue } from './hooks/useNurseSiteQueue';
import { canNurseAccessLocalPharmacy } from '../../constants/siteCareModes';
import { getNursingPortalConfig } from './nursingPortalConfig';
import Icon from '../common/Icons';
import '../receptionist/ReceptionistDashboard.css';
import './NurseDashboard.css';
import './NurseLayout.css';
import '../major/MajorPortal.css';

function readSidebarCollapsed(storageKey) {
  try {
    return localStorage.getItem(storageKey) === '1';
  } catch {
    return false;
  }
}

function NursingNavMenu({ items, collapsed = false, catalogSectionLabel = null }) {
  let catalogHeadingShown = false;

  return (
    <div className="nr-menu-section">
      <div className="nr-menu-section__items">
        {items.map(({ to, end, label, hint, icon, group }) => {
          const showCatalogHeading =
            catalogSectionLabel && group === 'catalog' && !catalogHeadingShown;
          if (showCatalogHeading) catalogHeadingShown = true;

          return (
            <React.Fragment key={to}>
              {showCatalogHeading ? (
                <div className="nr-menu-section__heading">{catalogSectionLabel}</div>
              ) : null}
              <NavLink
                to={to}
                end={!!end}
                title={collapsed ? label : undefined}
                className={({ isActive }) => `recv-menu-item nr-menu-item${isActive ? ' is-active' : ''}`}
              >
                <span className="recv-menu-item__icon nr-menu-item__icon" aria-hidden>
                  <Icon name={icon || 'home'} size={18} />
                </span>
                <span className="recv-menu-item__text nr-menu-item__text">
                  <span className="recv-menu-item__label nr-menu-item__label">{label}</span>
                  {hint ? <span className="recv-menu-item__hint nr-menu-item__hint">{hint}</span> : null}
                </span>
              </NavLink>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

export default function NursingPortalLayout({ variant = 'nurse' }) {
  const config = useMemo(() => getNursingPortalConfig(variant), [variant]);
  const { paths, copy, requiredRole, sidebarStorageKey, siteSessionPrefix, portalClassName, topbarSection } =
    config;

  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { ACCENT } = PLATFORM_BRANDING.NAME_PARTS;

  const { toasts, showToast, removeToast, getDuration } = useToast();
  const [workSites, setWorkSites] = useState([]);
  const [sitesLoading, setSitesLoading] = useState(true);
  const [sitesError, setSitesError] = useState('');
  const [workSiteId, setWorkSiteId] = useState('');
  const [statsReloadNonce, setStatsReloadNonce] = useState(0);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => readSidebarCollapsed(sidebarStorageKey));

  const siteSessionKey = useMemo(() => {
    const userKey = user?.id || user?.email || user?.username || 'anonymous';
    return `${siteSessionPrefix}:${userKey}`;
  }, [siteSessionPrefix, user?.email, user?.id, user?.username]);

  const toast = useCallback(
    (toastVariant, msg) => {
      showToast(msg, toastVariant);
    },
    [showToast]
  );

  const headerClock = useServerHeaderClock(Boolean(user));

  const loadWorkSites = useCallback(async () => {
    setSitesLoading(true);
    setSitesError('');
    try {
      const { sites } = await authService.getProfile();
      setWorkSites(Array.isArray(sites) ? sites : []);
    } catch (error) {
      setWorkSites([]);
      setSitesError(error.message || 'Impossible de charger vos sites de travail');
    } finally {
      setSitesLoading(false);
    }
  }, []);

  useEffect(() => {
    loadWorkSites();
  }, [loadWorkSites]);

  useEffect(() => {
    if (sitesLoading || workSiteId || workSites.length === 0) return;

    try {
      const storedSiteId = sessionStorage.getItem(siteSessionKey);
      if (storedSiteId && workSites.some((site) => String(site.id) === String(storedSiteId))) {
        setWorkSiteId(String(storedSiteId));
        try {
          sessionStorage.setItem(CLINICAL_WORK_SITE_SESSION_KEY, String(storedSiteId));
        } catch {
          /* ignore */
        }
        return;
      }
      sessionStorage.removeItem(siteSessionKey);
    } catch {
      /* ignore */
    }

    if (workSites.length === 1) {
      const onlySiteId = String(workSites[0].id);
      setWorkSiteId(onlySiteId);
      try {
        sessionStorage.setItem(siteSessionKey, onlySiteId);
        sessionStorage.setItem(CLINICAL_WORK_SITE_SESSION_KEY, onlySiteId);
      } catch {
        /* ignore */
      }
    }
  }, [siteSessionKey, sitesLoading, workSiteId, workSites]);

  const bumpDashboardStats = useCallback(() => {
    setStatsReloadNonce((n) => n + 1);
  }, []);

  const handleWorkSiteChange = useCallback(
    (id) => {
      setWorkSiteId(id);
      try {
        if (id) {
          sessionStorage.setItem(siteSessionKey, id);
          sessionStorage.setItem(CLINICAL_WORK_SITE_SESSION_KEY, id);
        } else {
          sessionStorage.removeItem(siteSessionKey);
          sessionStorage.removeItem(CLINICAL_WORK_SITE_SESSION_KEY);
        }
      } catch {
        /* ignore */
      }
    },
    [siteSessionKey]
  );

  const handleLogout = async () => {
    try {
      sessionStorage.removeItem(siteSessionKey);
      await logout();
    } catch (err) {
      console.error(err);
    }
  };

  const currentSite = useMemo(
    () => workSites.find((s) => String(s.id) === String(workSiteId)) || null,
    [workSites, workSiteId]
  );
  const showLocalPharmacy = canNurseAccessLocalPharmacy(currentSite);
  const navItems = useMemo(
    () => config.getNavItems({ showLocalPharmacy }),
    [config, showLocalPharmacy]
  );

  const queueState = useNurseSiteQueue(workSiteId, statsReloadNonce);

  const {
    consultations,
    loading,
    error,
    loadQueue,
    upsertConsultation,
    syncAfterPatientReceived,
    counts
  } = queueState;

  const consultationEditPath = useCallback(
    (consultationId, opts) => nursingConsultationEditPath(paths, consultationId, opts),
    [paths]
  );

  const filePath = useCallback((filterId) => nursingFilePath(paths, filterId), [paths]);

  const outletValue = useMemo(
    () => ({
      workSites,
      workSiteId,
      setWorkSiteId: handleWorkSiteChange,
      bumpDashboardStats,
      toast,
      statsReloadNonce,
      showLocalPharmacy,
      paths,
      copy,
      consultationEditPath,
      filePath,
      consultations,
      loading,
      error,
      loadQueue,
      upsertConsultation,
      syncAfterPatientReceived,
      counts
    }),
    [
      workSites,
      workSiteId,
      handleWorkSiteChange,
      bumpDashboardStats,
      toast,
      statsReloadNonce,
      showLocalPharmacy,
      paths,
      copy,
      consultationEditPath,
      filePath,
      consultations,
      loading,
      error,
      loadQueue,
      upsertConsultation,
      syncAfterPatientReceived,
      counts
    ]
  );

  if (!user || user.role !== requiredRole) {
    return null;
  }

  const initials =
    `${user?.last_name?.charAt(0) || ''}${user?.first_name?.charAt(0) || ''}`.toUpperCase() ||
    (user?.name || 'I').charAt(0).toUpperCase();

  const displayName =
    `${user?.last_name || ''}${user?.first_name ? ` ${user.first_name}` : ''}`.trim() ||
    user?.name ||
    copy.LAYOUT.ACCOUNT_FALLBACK;

  const chooseWorkSite = (site) => {
    handleWorkSiteChange(String(site.id));
    toast(
      'success',
      copy.LAYOUT.SITE_ACTIVE_TOAST.replace('{name}', site.name)
    );
  };

  const resetWorkSite = () => {
    handleWorkSiteChange('');
  };

  const toggleSidebar = () => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(sidebarStorageKey, next ? '1' : '0');
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  const layoutClassName = [
    portalClassName,
    'receptionist-dashboard',
    'receptionist-layout',
    'min-vh-100',
    'd-flex',
    'flex-column',
    sidebarCollapsed ? 'nr-sidebar-is-collapsed' : ''
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={layoutClassName}>
      <Navbar variant="dark" className="recv-topbar nr-topbar flex-shrink-0 recv-no-print shadow-sm">
        <Container fluid className="recv-topbar__container nr-topbar__container">
          <Navbar.Brand
            className="recv-topbar__brand-wrap nr-topbar__brand-wrap mb-0"
            onClick={() => navigate(paths.DASHBOARD)}
            style={{ cursor: 'pointer' }}
          >
            <img
              src={PLATFORM_BRANDING.LOGO_WORDMARK_SRC}
              alt={PLATFORM_BRANDING.LOGO_WORDMARK_ALT}
              className="recv-topbar__wordmark nr-topbar__wordmark"
            />
            <span className="recv-topbar__brand nr-topbar__brand">
              <span className="recv-topbar__brand-product nr-topbar__brand-product">{ACCENT}</span>
              <span className="recv-topbar__brand-section nr-topbar__brand-section">{topbarSection}</span>
            </span>
          </Navbar.Brand>

          <div className="recv-topbar__toolbar nr-topbar__toolbar ms-auto d-flex align-items-center gap-2">
            {currentSite ? (
              <button
                type="button"
                className="recv-site-chip mx-lg-2 my-2 my-lg-0"
                onClick={resetWorkSite}
                title={copy.LAYOUT.CHANGE_SITE_CHIP_TITLE}
              >
                <span className="recv-site-chip__label">{copy.LAYOUT.SITE_CHIP_LABEL}</span>
                <span className="recv-site-chip__name">{currentSite.name}</span>
              </button>
            ) : workSites.length === 0 && !sitesLoading ? (
              <span className="text-white-50 small px-2">{copy.LAYOUT.NO_SITE_ASSIGNED}</span>
            ) : null}
            <span
              className="recv-topbar__clock d-none d-md-inline recv-header-clock"
              title={copy.LAYOUT.HEADER_CLOCK_TITLE}
              aria-live="polite"
            >
              {headerClock}
            </span>
            <NavDropdown
              align="end"
              className="recv-user-menu nr-user-menu"
              title={
                <span className="recv-user-menu__trigger" aria-label={copy.LAYOUT.ACCOUNT_MENU_ARIA}>
                  <span className="recv-user-menu__avatar" aria-hidden>
                    {initials}
                  </span>
                  <span className="recv-user-menu__meta d-none d-md-flex">
                    <span className="recv-user-menu__name">{displayName}</span>
                    <span className="recv-user-menu__role">{getRoleLabel(user?.role)}</span>
                  </span>
                  <Icon name="chevronDown" size={14} className="recv-user-menu__chevron d-none d-md-block" aria-hidden />
                </span>
              }
              id={config.userMenuId}
            >
              <NavDropdown.Header className="recv-user-menu__header">
                <span className="recv-user-menu__header-avatar" aria-hidden>
                  {initials}
                </span>
                <span className="recv-user-menu__header-text">
                  <span className="recv-user-menu__header-name">{displayName}</span>
                  {user?.email ? <span className="recv-user-menu__header-email">{user.email}</span> : null}
                  <span className="recv-user-menu__role-badge">{getRoleLabel(user?.role)}</span>
                </span>
              </NavDropdown.Header>
              {currentSite ? (
                <>
                  <NavDropdown.Divider className="recv-user-menu__divider" />
                  <NavDropdown.Item className="recv-user-menu__action" onClick={resetWorkSite}>
                    {copy.LAYOUT.CHANGE_SITE_MENU}
                  </NavDropdown.Item>
                </>
              ) : null}
              <NavDropdown.Divider className="recv-user-menu__divider" />
              <NavDropdown.Item
                className="recv-user-menu__action d-lg-none"
                onClick={() => navigate(paths.GUIDE)}
              >
                {copy.NAV.aide.label}
              </NavDropdown.Item>
              <NavDropdown.Divider className="recv-user-menu__divider d-lg-none" />
              <NavDropdown.Item className="recv-user-menu__action recv-user-menu__action--danger" onClick={handleLogout}>
                <Icon name="signOut" size={16} aria-hidden />
                {copy.LAYOUT.LOGOUT}
              </NavDropdown.Item>
            </NavDropdown>
          </div>
        </Container>
      </Navbar>

      {!currentSite ? (
        <main className="recv-site-gate flex-grow-1 overflow-auto">
          <Container fluid className="recv-site-gate__container">
            <section className="recv-site-gate__panel" aria-labelledby="nursing-site-gate-title">
              <div className="recv-site-gate__intro">
                <span className="recv-site-gate__badge">{config.siteGateBadge}</span>
                <h1 id="nursing-site-gate-title">{copy.LAYOUT.SITE_GATE_TITLE}</h1>
                <p>{copy.LAYOUT.SITE_GATE_DESC}</p>
              </div>

              {sitesLoading ? (
                <div className="recv-site-gate__state">
                  <span className="spinner-border spinner-border-sm" aria-hidden />
                  {copy.LAYOUT.SITES_LOADING}
                </div>
              ) : sitesError ? (
                <div className="recv-site-gate__state recv-site-gate__state--error">
                  <span>{sitesError}</span>
                  <button type="button" className="btn btn-primary btn-sm" onClick={loadWorkSites}>
                    Réessayer
                  </button>
                </div>
              ) : workSites.length === 0 ? (
                <div className="recv-site-gate__state recv-site-gate__state--error">
                  Aucun site de travail n&apos;est affecté à votre profil. Contactez un administrateur pour vous
                  attribuer au moins un site actif.
                </div>
              ) : (
                <div className="recv-site-grid" role="list">
                  {workSites.map((site) => (
                    <button
                      key={site.id}
                      type="button"
                      className="recv-site-card"
                      onClick={() => chooseWorkSite(site)}
                      role="listitem"
                    >
                      <span className="recv-site-card__icon" aria-hidden>
                        <Icon name="building" size={18} />
                      </span>
                      <span className="recv-site-card__body">
                        <span className="recv-site-card__name">{site.name}</span>
                        <span className="recv-site-card__meta">
                          {site.address || site.location || copy.LAYOUT.SITE_CARD_META_FALLBACK}
                        </span>
                      </span>
                      <span className="recv-site-card__cta">{copy.LAYOUT.SITE_CARD_CTA}</span>
                    </button>
                  ))}
                </div>
              )}
            </section>
          </Container>
        </main>
      ) : (
        <>
          <div className="d-lg-none recv-mobile-nav nr-mobile-nav border-bottom bg-white flex-shrink-0 recv-no-print">
            <Container fluid className="py-2">
              <div className="d-flex gap-2 flex-wrap align-items-center">
                {navItems.map(({ to, end, label }) => (
                  <NavLink
                    key={to}
                    to={to}
                    end={!!end}
                    className={({ isActive }) => `recv-mobile-pill ${isActive ? ' is-active' : ''}`}
                  >
                    {label}
                  </NavLink>
                ))}
              </div>
            </Container>
          </div>

          <div className="d-flex flex-grow-1 overflow-hidden recv-workspace">
            <aside
              className={`recv-sidebar nr-sidebar d-none d-lg-flex flex-column flex-shrink-0 recv-no-print${
                sidebarCollapsed ? ' is-collapsed' : ''
              }`}
              aria-label={copy.LAYOUT.SIDE_MENU_SECTION}
            >
              <div className="nr-sidebar__brand">
                <span className="nr-sidebar__brand-label">FUNTOA SMIE</span>
                <span className="nr-sidebar__brand-title">{copy.LAYOUT.SIDE_MENU_SECTION}</span>
              </div>
              <nav className="recv-sidebar__nav nr-sidebar__nav" aria-label={copy.LAYOUT.SIDEBAR_NAV_ARIA}>
                <NursingNavMenu
                  items={navItems}
                  collapsed={sidebarCollapsed}
                  catalogSectionLabel={config.catalogSectionLabel}
                />
              </nav>

              <div className="recv-sidebar__footer">
                <button
                  type="button"
                  className="recv-sidebar__toggle"
                  onClick={toggleSidebar}
                  aria-expanded={!sidebarCollapsed}
                  aria-label={
                    sidebarCollapsed
                      ? copy.LAYOUT.SIDEBAR_TOGGLE_COLLAPSED
                      : copy.LAYOUT.SIDEBAR_TOGGLE_EXPANDED
                  }
                >
                  <Icon name={sidebarCollapsed ? 'chevronRight' : 'chevronLeft'} size={18} aria-hidden />
                  <span className="recv-sidebar__toggle-label">
                    {sidebarCollapsed
                      ? copy.LAYOUT.SIDEBAR_TOGGLE_COLLAPSED_SHORT
                      : copy.LAYOUT.SIDEBAR_TOGGLE_EXPANDED_SHORT}
                  </span>
                </button>
              </div>
            </aside>

            <main className="recv-main flex-grow-1 overflow-auto">
              <Container fluid className="py-4 px-md-4 recv-dash-container nr-dash-container">
                <NurseWorkspaceContext.Provider value={outletValue}>
                  <Outlet />
                </NurseWorkspaceContext.Provider>
              </Container>
            </main>
          </div>
        </>
      )}

      <ReceptionToastStack toasts={toasts} onRemove={removeToast} getDuration={getDuration} />
    </div>
  );
}
